import { useOutletContext } from 'react-router-dom';
import type { TenantOutletContext } from './tenant-layout';

/**
 * Bare `/t/:tenantId/` landing — the tenant Overview.
 *
 * Deliberately minimal for Slice 2. Real metrics (ceremonies by
 * status over time, funnel, spend, etc.) and eventually
 * configurable dashboard layouts land in later slices; over-investing
 * here would block the product on a feature we do not yet know the
 * shape of. See `apps/console/README.md` → Destination shape →
 * "Metrics" (gated on a metrics model + design work).
 */
export function TenantOverviewPage() {
  const { tenant } = useOutletContext<TenantOutletContext>();

  return (
    <div className="flex flex-col gap-8">
      <div>
        <h2 className="font-serif text-xl tracking-tight text-neutral-900">
          Overview
        </h2>
        <p className="mt-1 text-sm text-neutral-600">
          A quick read of the tenant. Richer metrics land in a later slice.
        </p>
      </div>

      <dl className="grid max-w-lg grid-cols-[max-content_1fr] gap-x-6 gap-y-2 text-sm">
        <dt className="text-neutral-500">Display name</dt>
        <dd className="text-neutral-900">{tenant.displayName}</dd>
        <dt className="text-neutral-500">Id</dt>
        <dd className="font-mono text-neutral-700">{tenant.id}</dd>
        <dt className="text-neutral-500">Created</dt>
        <dd className="text-neutral-700">{formatDate(tenant.createdAtMs)}</dd>
        <dt className="text-neutral-500">Updated</dt>
        <dd className="text-neutral-700">{formatDate(tenant.updatedAtMs)}</dd>
      </dl>

      <div className="rounded border border-dashed border-neutral-300 bg-neutral-50 px-4 py-6 text-sm text-neutral-500">
        Metrics will appear here in a later slice — ceremonies by status
        over time, completion funnel, and other tenant-level signals.
      </div>
    </div>
  );
}

function formatDate(ms: number): string {
  return new Date(ms).toISOString().replace('T', ' ').slice(0, 19) + ' UTC';
}
